import {
  getCategorizedPermissions,
  getAndroidVersionName,
} from '../../utils/androidUtils';
import type { HubspotAutomatedReviewSummary } from '../../stores/hubspotManagementStore';
import { getMappedStatusBadge } from '../../utils/hubspotUtils';
import type {
  SolanaDappStoreData,
  HubspotTicketDetailData,
  HubspotTicketAutomatedReviewData,
  HubspotTicketAutomatedReviewResponse,
  HubspotTicketDerivedData,
  HubspotTicketDetailResponse,
  MediaLoadingState,
} from './types';
import { getTokenReleaseSolanaDappStore } from '../../utils/nftMetadataHelpers';
import { isVideoMimeType } from '../../utils/imageDimensions';
import { getMediaType } from '../../utils/mediaUtils';

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return typeof value === 'object' && value !== null
    ? (value as Record<string, unknown>)
    : undefined;
}

function normalizeAutomatedReviewSummary(
  summary: Omit<HubspotAutomatedReviewSummary, 'lastCompletedAt'> & {
    lastCompletedAt: Date | string | null;
  },
): HubspotAutomatedReviewSummary {
  return {
    ...summary,
    lastCompletedAt: summary.lastCompletedAt
      ? new Date(summary.lastCompletedAt)
      : null,
  };
}

export function normalizeTicketDetail(
  ticket: HubspotTicketDetailResponse,
): HubspotTicketDetailData {
  return {
    ...ticket,
    tokenJson: ticket.tokenJson ?? null,
    properties: ticket.properties ?? null,
    createdAt: new Date(ticket.createdAt),
    lastModifiedAt: new Date(ticket.lastModifiedAt),
    closedDate: ticket.closedDate ? new Date(ticket.closedDate) : null,
    automatedReviewSummary: ticket.automatedReviewSummary
      ? normalizeAutomatedReviewSummary(ticket.automatedReviewSummary)
      : null,
    latestAutomatedReviewSummary: ticket.latestAutomatedReviewSummary
      ? normalizeAutomatedReviewSummary(ticket.latestAutomatedReviewSummary)
      : null,
    closureDetails: ticket.closureDetails
      ? {
          ...ticket.closureDetails,
          reviewedAt: ticket.closureDetails.reviewedAt
            ? new Date(ticket.closureDetails.reviewedAt)
            : null,
        }
      : null,
    internalNotes: ticket.internalNotes.map((note) => ({
      ...note,
      createdAt: new Date(note.createdAt),
    })),
    auditEntries: ticket.auditEntries.map((entry) => ({
      ...entry,
      createdAt: new Date(entry.createdAt),
    })),
  };
}

export function normalizeAutomatedReview(
  review: HubspotTicketAutomatedReviewResponse,
): HubspotTicketAutomatedReviewData {
  return {
    ...review,
    startedAt: review.startedAt ? new Date(review.startedAt) : null,
    completedAt: review.completedAt ? new Date(review.completedAt) : null,
    checks: review.checks.map((check) => ({
      ...check,
      startedAt: check.startedAt ? new Date(check.startedAt) : null,
      completedAt: check.completedAt ? new Date(check.completedAt) : null,
    })),
  };
}

export function getHubspotTicketDerivedData(
  data: HubspotTicketDetailData | null,
): HubspotTicketDerivedData {
  const tokenData = asRecord(asRecord(data?.tokenJson)?.data);
  const dappStoreData = getTokenReleaseSolanaDappStore<SolanaDappStoreData>(
    data?.tokenJson,
  );
  const androidDetails = dappStoreData?.android_details;
  const media = (dappStoreData?.media ??
    []) as HubspotTicketDerivedData['screenshots'];
  const isVideo = (item: (typeof media)[number]) =>
    item.purpose === 'video' ||
    isVideoMimeType(item.mime) ||
    getMediaType(item.uri) === 'video';
  const updateAuthority = tokenData?.nft_collection_update_authority;

  return {
    dappStoreData,
    androidDetails,
    categorizedPermissions: getCategorizedPermissions(
      androidDetails?.permissions,
    ),
    statusBadge: data
      ? getMappedStatusBadge(data.mappedSubmissionStatus)
      : null,
    banner: media.find((item) => item.purpose === 'banner'),
    featureGraphic: media.find((item) => item.purpose === 'featureGraphic'),
    screenshots: media.filter(
      (item) => item.purpose === 'screenshot' && !isVideo(item),
    ),
    videos: media.filter(
      (item) =>
        (item.purpose === 'screenshot' || item.purpose === 'video') &&
        isVideo(item),
    ),
    files: dappStoreData?.files ?? [],
    updateAuthority:
      typeof updateAuthority === 'string' ? updateAuthority : undefined,
  };
}

export function createInitialMediaLoadingState(): MediaLoadingState {
  return {
    banner: true,
    featureGraphic: true,
    screenshots: new Set<number>(),
    videos: true,
  };
}

export function createMediaLoadingState({
  banner,
  featureGraphic,
  screenshots,
  videos,
}: Pick<
  HubspotTicketDerivedData,
  'banner' | 'featureGraphic' | 'screenshots' | 'videos'
>): MediaLoadingState {
  return {
    banner: !!banner,
    featureGraphic: !!featureGraphic,
    screenshots:
      screenshots.length > 0
        ? new Set(screenshots.map((_, index) => index))
        : new Set<number>(),
    videos: videos.length > 0,
  };
}

export function clearScreenshotLoadingIndex(
  screenshots: Set<number>,
  index: number,
): Set<number> {
  const next = new Set(screenshots);
  next.delete(index);
  return next;
}

export { getAndroidVersionName };
